import * as Yup from 'yup';
import common from 'constants/common';

export default Yup.object().shape(
  {
    title: Yup.string().required('required').typeError('required'),
    firstName: Yup.string()
      .required('required')
      .test(
        'is-not-only-spaces',
        'First Name is not valid',
        (value) => !!value && value.trim().length > 0,
      ),
    lastName: Yup.string()
      .required('required')
      .test(
        'is-not-only-spaces',
        'Last Name is not valid',
        (value) => !!value && value.trim().length > 0,
      ),
    birthDate: Yup.string()
      .matches(
        /^(0[1-9]|1[012])[-]([012][0-9]|3[01])[-]([0-9]{4})$/,
        'Birth Date must be in the format MM-DD-YYYY.',
      )
      .required('required'),
    phone: Yup.string()
      .matches(/^[+][0-9]*$/, 'Phone number is not correct')
      .required('required'),
    homeNumber: Yup.string()
      .matches(/^[+][0-9]*$/, 'Phone number is not correct')
      .nullable(),
    email: Yup.string().email('Email is not correct').required('required'),
    clinicGuid: Yup.string().required('required'),
    doctorGuid: Yup.string().required('required'),
    insurances: Yup.array().of(
      Yup.object().shape({
        planName: Yup.string().nullable(),
        provider: Yup.string()
          .required('required')
          .test(
            'is-not-only-spaces',
            'Provider is not valid',
            (value) => !!value && value.trim().length > 0,
          ),
        number: Yup.string()
          .required('required')
          .test(
            'is-not-only-spaces',
            'Number is not valid',
            (value) => !!value && value.trim().length > 0,
          ),
        type: Yup.string().nullable(),
      }),
    ),
    devices: Yup.array().of(
      Yup.object().shape({
        action: Yup.string().nullable(),
        manufacturer: Yup.string()
          .when('action', {
            is: (action) => action === 'delete',
            then: (schema) => schema.nullable(),
            otherwise: (schema) => schema.required('required'),
          })
          .test(
            'is-not-only-spaces',
            'Device Name is not valid',
            (value) => !!value && value.trim().length > 0,
          ),
        deviceId: Yup.string()
          .required('required')
          .test(
            'is-not-only-spaces',
            'Number is not valid',
            (value) => !!value && value.trim().length > 0,
          ),
        readingTypes: Yup.array()
          .of(
            Yup.object().shape({
              value: Yup.string(),
              id: Yup.string(),
              label: Yup.string(),
            }),
          )
          .when('action', {
            is: (action) => action === 'delete',
            then: (schema) => schema.nullable(),
            otherwise: (schema) => schema.required('required'),
          }),
      }),
    ),
    priorAuths: Yup.array()
      .of(
        Yup.object().shape({
          authorizationNumber: Yup.number()
            .min(0, 'Authorization number must be greater than or equal to 0')
            .required('required'),
          startDate: Yup.date()
            .typeError('Invalid date value')
            .when('authorizationNumber', {
              is: (authorizationNumber) => !!authorizationNumber,
              then: Yup.date()
                .typeError('Invalid date value')
                .required('required'),
            }),
          endDate: Yup.date()
            .typeError('Invalid date value')
            .min(Yup.ref('startDate'), "end date can't be before start date")
            .when('authorizationNumber', {
              is: (authorizationNumber) => !!authorizationNumber,
              then: Yup.date()
                .typeError('Invalid date value')
                .required('required'),
            }),
        }),
      )
      .default([]),
    gender: Yup.string().nullable(),
    ethnicity: Yup.string().nullable(),
    address: Yup.string().when('zipPostalCode', {
      is: (zipPostalCode) => !!zipPostalCode,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
    city: Yup.string().when('zipPostalCode', {
      is: (zipPostalCode) => !!zipPostalCode,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
    state: Yup.string().when(['zipPostalCode', 'city'], {
      is: (zipPostalCode, city) => !!zipPostalCode || !!city,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
    zipPostalCode: Yup.string().when(['address', 'state'], {
      is: (address, state) => !!address || !!state,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
    race: Yup.string().nullable(),
    preferredContactMethod: Yup.string().nullable(),
    status: Yup.string(),
    ccmStatus: Yup.string(),
    pcmStatus: Yup.boolean(),
    workingStatus: Yup.string().nullable(),
    martialStatus: Yup.string().nullable(),
    emergencyContactNumber: Yup.string()
      .matches(/^[+][0-9]*$/, 'Emergency contact number is not correct')
      .nullable(),
    emrName: Yup.string().nullable(),
    emrId1: Yup.string().nullable(),
    emrId2: Yup.string().nullable(),
    supervisingProviderId: Yup.string().nullable(),
    renderingProviderId: Yup.string().nullable(),
    readingsFlag: Yup.boolean().nullable(),
    billing: Yup.boolean().nullable(),
    billingCycle: Yup.string().nullable(),
    reports: Yup.string().nullable(),
    account: Yup.string().nullable(),
    minCriticalSystolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(
        Yup.ref('maxCriticalSystolic'),
        'Must be less than Max Critical',
      ),
    maxCriticalSystolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(
        Yup.ref('minCriticalSystolic'),
        'Must be more than Min Critical',
      ),
    minRiskSystolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(Yup.ref('maxRiskSystolic'), 'Must be less than Max at Risk')
      .moreThan(
        Yup.ref('minCriticalSystolic'),
        'Must be more than Min Critical',
      ),
    maxRiskSystolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(Yup.ref('minRiskSystolic'), 'Must be more than Min at Risk')
      .lessThan(
        Yup.ref('maxCriticalSystolic'),
        'Must be less than Max Critical',
      ),
    minCriticalDiastolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(
        Yup.ref('maxCriticalDiastolic'),
        'Must be less than Max Critical',
      ),
    maxCriticalDiastolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(
        Yup.ref('minCriticalDiastolic'),
        'Must be more than Min Critical',
      ),
    minRiskDiastolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(Yup.ref('maxRiskDiastolic'), 'Must be less than Max at Risk')
      .moreThan(
        Yup.ref('minCriticalDiastolic'),
        'Must be more than Min Critical',
      ),
    maxRiskDiastolic: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(Yup.ref('minRiskDiastolic'), 'Must be more than Min at Risk')
      .lessThan(
        Yup.ref('maxCriticalDiastolic'),
        'Must be less than Max Critical',
      ),
    minCriticalPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(Yup.ref('maxCriticalPulse'), 'Must be less than Max Critical'),
    maxCriticalPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(Yup.ref('minCriticalPulse'), 'Must be more than Min Critical'),
    minCriticalSleepPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(
        Yup.ref('maxCriticalSleepPulse'),
        'Must be less than Max Critical',
      ),
    maxCriticalSleepPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(
        Yup.ref('minCriticalSleepPulse'),
        'Must be more than Min Critical',
      ),
    minRiskPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(Yup.ref('maxRiskPulse'), 'Must be less than Max at Risk')
      .moreThan(Yup.ref('minCriticalPulse'), 'Must be more than Min Critical'),
    maxRiskPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(Yup.ref('minRiskPulse'), 'Must be more than Min at Risk')
      .lessThan(Yup.ref('maxCriticalPulse'), 'Must be less than Max Critical'),
    minRiskSleepPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .lessThan(Yup.ref('maxRiskSleepPulse'), 'Must be less than Max at Risk')
      .moreThan(
        Yup.ref('minCriticalSleepPulse'),
        'Must be more than Min Critical',
      ),
    maxRiskSleepPulse: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(200, 'Must be less than 200')
      .moreThan(Yup.ref('minRiskSleepPulse'), 'Must be more than Min at Risk')
      .lessThan(
        Yup.ref('maxCriticalSleepPulse'),
        'Must be less than Max Critical',
      ),
    criticalBloodoxygen: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(100, 'Must be less than 100')
      .lessThan(Yup.ref('riskBloodoxygen'), 'Must be less than At Risk'),
    riskBloodoxygen: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(100, 'Must be less than 100')
      .moreThan(Yup.ref('criticalBloodoxygen'), 'Must be more than Critical'),
    minCriticalGlucose: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(400, 'Must be less than 400')
      .lessThan(Yup.ref('minRiskGlucose'), 'Must be less than Min Risk'),
    maxCriticalGlucose: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(400, 'Must be less than 400')
      .moreThan(
        Yup.ref('minCriticalGlucose'),
        'Must be more than Min Critical',
      ),
    minRiskGlucose: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(400, 'Must be less than 400')
      .lessThan(Yup.ref('maxRiskGlucose'), 'Must be less than Max at Risk')
      .moreThan(
        Yup.ref('minCriticalGlucose'),
        'Must be more than Min Critical',
      ),
    maxRiskGlucose: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(400, 'Must be less than 400')
      .moreThan(Yup.ref('minRiskGlucose'), 'Must be more than Min at Risk')
      .lessThan(
        Yup.ref('maxCriticalGlucose'),
        'Must be less than Max Critical',
      ),
    baseLineWeight: Yup.number().min(0, 'Must be more than 0').nullable(),
    maxCriticalWeight: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .moreThan(Yup.ref('maxRiskWeight'), 'Must be more than Max Risk'),
    minRiskWeight: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(400, 'Must be less than 400'),
    maxRiskWeight: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .max(400, 'Must be less than 400'),
    auth0Enabled: Yup.boolean()
      .required()
      .test(
        'is-false',
        `This fields must be "no" when the email field is ends with ${common.disabledEmailDomain}`,
        // eslint-disable-next-line func-names
        function (value) {
          return (
            // eslint-disable-next-line react/no-this-in-sfc
            !value || !this.parent.email.includes(common.disabledEmailDomain)
          );
        },
      ),
    minRiskPef: Yup.number()
      .integer()
      .min(0, 'Must be more than 0')
      .moreThan(Yup.ref('minCriticalPef'), 'Must be more than Min Critical'),
    minCriticalPef: Yup.number().integer().min(0, 'Must be more than 0'),
    minCriticalFev1: Yup.number().min(0, 'Must be more than 0'),
  },
  [
    ['address', 'zipPostalCode'],
    ['zipPostalCode', 'state'],
  ],
);
